<script setup lang="ts">
import type { VideoCardProps } from '.';

import { useEvents } from '~/composables/events/useEvents';
import { useToggleFavoriteSync } from '~/composables/useToggleFavoriteSync';
import { MixpanelProps } from '~/core/service/mixpanel';

import { usePropertyInfo } from './usePropertyInfo';
import { useVideoCardPlayer } from './useVideoCardPlayer';
import VideoCardFooter from './VideoCardFooter.vue';
import VideoCardHeader from './VideoCardHeader.vue';
import VideoCardLoadingBar from './VideoCardLoadingBar.vue';
import VideoCardPlayIndicator from './VideoCardPlayIndicator.vue';

const MOCK_AGENT = {
	name: 'Maria Silva',
	profilePictureUrl: 'https://i.pravatar.cc/150?u=broker-mock'
};

const MOCK_COMPANY = {
	name: 'Pilar Imobiliária',
	logoUrl: 'https://i.pravatar.cc/150?u=company-mock'
};

const textProtectionOverlayStyle = {
	background:
		'linear-gradient(180deg, rgba(0, 0, 0, 0.3) 11.48%, rgba(0, 0, 0, 0) 31.75%, rgba(0, 0, 0, 0) 58.79%, rgba(0, 0, 0, 0.3) 79.07%)'
};

const props = withDefaults(defineProps<VideoCardProps>(), {
	isActive: false,
	hoverToPlay: true,
	imgEagerCount: 3,
	imgPreloadCount: 0,
	playerRootMargin: '100px',
	sectionName: 'shorts'
});

const emit = defineEmits<{
	click: [index: number];
}>();

const route = useRoute();
const property = computed(() => props.property);

const {
	cardRef,
	videoRef,
	isVideoPlaying,
	isVisible,
	isLoading,
	shouldPlay,
	videoProgress,
	isMuted,
	handleMuteToggle,
	seekTo
} = useVideoCardPlayer({
	videoUrl: computed(() => props.videoUrl),
	isActive: computed(() => props.isActive),
	hoverToPlay: computed(() => props.hoverToPlay),
	index: computed(() => props.index),
	playerRootMargin: props.playerRootMargin
});

const {
	formattedPrice,
	formattedArea,
	areaLabel,
	roomsInfo,
	region,
	cityState
} = usePropertyInfo(property);

const propertyId = computed(() => props.property.id);
const commercialId = computed(() => props.property.commercialId);
const sectionName = toRef(props, 'sectionName');

const favoriteEventData = computed(() => ({
	currentPage: route.fullPath,
	companyName: props.property.company?.name,
	agentName: props.property.agent?.name,
	tagExclusive: props.property.isExclusive ?? false,
	clickCard: true
}));

const {
	isFavorite,
	handleToggleFavorite,
	isValidData: isFavoriteValid
} = useToggleFavoriteSync(
	propertyId,
	commercialId,
	favoriteEventData,
	sectionName
);

const agentInfo = computed(() => props.property.agent ?? MOCK_AGENT);
const companyInfo = computed(() => props.property.company ?? MOCK_COMPANY);

const CARD_TYPE = 'shorts card';

/**
 * Alinhado ao carrossel da `ShortsSection`: basis 94% / 48% / 32% / 24% (variant dark).
 * O seletor responsivo é `sizes`; width/height são teto (~2× slot no mobile) + ratio 9/14 para o IPX.
 */
const REEL_THUMB_SIZES =
	'(max-width: 767px) 94vw, (max-width: 1023px) 48vw, (max-width: 1279px) 32vw, 24vw';
const REEL_THUMB_WIDTH = 840;
const REEL_THUMB_HEIGHT = Math.round((REEL_THUMB_WIDTH * 14) / 9);

const { trackPropertyCardClicked } = useEvents();

function handleClick() {
	trackPropertyCardClicked({
		[MixpanelProps.COMMERCIAL_ID]: props.property.commercialId,
		[MixpanelProps.DESTINATION_URL]: `/shorts/${props.property.commercialId}`,
		[MixpanelProps.CURRENT_PAGE]: route.fullPath,
		[MixpanelProps.SECTION_NAME]: sectionName.value,
		[MixpanelProps.CARD_POSITION]: props.index,
		[MixpanelProps.CARD_TYPE]: CARD_TYPE,
		[MixpanelProps.TAG_EXCLUSIVE]: props.property.isExclusive ?? false,
		[MixpanelProps.COMPANY_NAME]: props.property.company?.name ?? '',
		[MixpanelProps.AGENT_NAME]: props.property.agent?.name ?? ''
	});
	emit('click', props.index);
}
</script>

<template>
	<div
		ref="cardRef"
		class="video-card group relative aspect-9/14 h-full w-full cursor-pointer overflow-hidden bg-primary-light-4 transition-shadow duration-200"
		@click="handleClick"
	>
		<div
			:class="[
				'absolute inset-0 transition-[transform,opacity] duration-300',
				isVideoPlaying ? 'scale-105 opacity-0' : 'scale-100 opacity-100'
			]"
		>
			<NuxtImg
				v-if="props.thumbnailUrl"
				:src="props.thumbnailUrl"
				:alt="`Vídeo do imóvel ${property.commercialId}`"
				class="size-full object-cover"
				:width="REEL_THUMB_WIDTH"
				:height="REEL_THUMB_HEIGHT"
				:sizes="REEL_THUMB_SIZES"
				format="webp"
				quality="85"
				:loading="props.index < props.imgEagerCount ? 'eager' : 'lazy'"
				:preload="
					props.imgPreloadCount > 0 && props.index < props.imgPreloadCount
				"
			/>
			<div v-else class="size-full bg-primary-light-4" />
		</div>

		<video
			v-if="isVisible"
			ref="videoRef"
			:class="[
				'pointer-events-none absolute inset-0 size-full object-cover transition-opacity duration-300',
				isVideoPlaying ? 'opacity-100' : 'opacity-0'
			]"
			:muted="isMuted"
			playsinline
			loop
		/>

		<div
			class="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,_rgba(0,0,0,0.3)_11.48%,_rgba(0,0,0,0)_31.75%,_rgba(0,0,0,0)_58.79%,_rgba(0,0,0,0.3)_79.07%)]"
		/>

		<VideoCardHeader
			v-model:is-favorite="isFavorite"
			:agent-info="agentInfo"
			:company-info="companyInfo"
			:property-id="property.id"
			:commercial-id="property.commercialId"
			:is-muted="isMuted"
			:is-favorite-valid="isFavoriteValid"
			:is-visible="shouldPlay"
			:hide-agent-info="false"
			@toggle-mute="handleMuteToggle"
			@toggle-favorite="handleToggleFavorite"
		/>

		<VideoCardPlayIndicator
			:is-loading="isLoading"
			:is-video-playing="isVideoPlaying"
			:is-active="shouldPlay"
		/>

		<VideoCardLoadingBar v-if="isLoading && !isVideoPlaying" />

		<VideoCardFooter
			:formatted-price="formattedPrice"
			:formatted-area="formattedArea"
			:area-label="areaLabel"
			:rooms-info="roomsInfo"
			:parking-spots="property.parkingSpots"
			:region="region"
			:city-state="cityState"
			:video-progress="videoProgress"
			:is-video-playing="isVideoPlaying"
			@seek="seekTo"
		/>
	</div>
</template>
